/**
 * useEcadZoomPan — Zoom + Edge-Pan controller for ECAD Recorder
 *
 * Phase 3: Integrates with RecorderHealthAPI for resource tracking.
 * - Pan RAF loop registered via health.trackRAF('recording', 'zoomPanLoop')
 * - Emergency mode stops pan loop immediately
 */
import { useState, useEffect, useRef } from 'react';
import type { RecorderHealthAPI } from './useRecorderHealth';


export function useEcadZoomPan(
  cursorPosRef: React.MutableRefObject<{ x: number; y: number }>,
  status: string,
  healthApi?: RecorderHealthAPI
) {
  const [zoomEnabled, setZoomEnabled] = useState(true);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomScale, setZoomScale] = useState(() => {
    const saved = localStorage.getItem('ecad_zoom_scale');
    return saved ? Math.max(1.2, Math.min(4.0, Number(saved))) : 1.8;
  });
  const [zoomTransitioning, setZoomTransitioning] = useState(false);
  const [panTrigger, setPanTrigger] = useState(0);

  const zoomTxRef = useRef(0);
  const zoomTyRef = useRef(0);
  const panVelocityRef = useRef({ vx: 0, vy: 0 });
  const panTimeRef = useRef(0);
  const animationFrameIdRef = useRef<number | null>(null);

  // Stable references for use in useEffect/event listeners
  const statusRef = useRef(status);
  useEffect(() => { statusRef.current = status; }, [status]);

  const zoomScaleRef = useRef(zoomScale);
  useEffect(() => { zoomScaleRef.current = zoomScale; }, [zoomScale]);

  const isZoomedRef = useRef(isZoomed);
  useEffect(() => { isZoomedRef.current = isZoomed; }, [isZoomed]);

  const zoomEnabledRef = useRef(zoomEnabled);
  useEffect(() => { zoomEnabledRef.current = zoomEnabled; }, [zoomEnabled]);

  useEffect(() => {
    if (isZoomed) {
      const W = window.innerWidth;
      const H = statusRef.current === 'recording' ? window.innerHeight : window.innerHeight - 56;
      const scaleVal = zoomScaleRef.current;
      const limitX = W * (scaleVal - 1) / (2 * scaleVal);
      const limitY = H * (scaleVal - 1) / (2 * scaleVal);
      zoomTxRef.current = Math.max(-limitX, Math.min(limitX, W / 2 - cursorPosRef.current.x));
      zoomTyRef.current = Math.max(-limitY, Math.min(limitY, H / 2 - cursorPosRef.current.y));
      
      setZoomTransitioning(true);
      const timer = setTimeout(() => {
        setZoomTransitioning(false);
      }, 350);
      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => {
        zoomTxRef.current = 0;
        zoomTyRef.current = 0;
      }, 350);
      setZoomTransitioning(false);
      return () => clearTimeout(timer);
    }
  }, [isZoomed]);

  useEffect(() => {
    if (!isZoomed || !zoomEnabled) {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        if (healthApi) healthApi.untrackRAF('zoomPanLoop');
        animationFrameIdRef.current = null;
      }
      panVelocityRef.current = { vx: 0, vy: 0 };
      panTimeRef.current = 0;
      return;
    }

    const W = window.innerWidth;
    const H = statusRef.current === 'recording' ? window.innerHeight : window.innerHeight - 56;
    const y_offset = (statusRef.current === 'recording') ? 0 : 56;

    const edgeThreshold = 200;
    const maxSpeed = 6;
    const easing = 0.04;

    const updatePanning = () => {
      // Emergency guard — stop panning immediately
      if (healthApi && healthApi.isPhase('EMERGENCY')) {
        animationFrameIdRef.current = null;
        panVelocityRef.current = { vx: 0, vy: 0 };
        return;
      }

      const mouse = cursorPosRef.current;
      if (!mouse) {
        animationFrameIdRef.current = requestAnimationFrame(updatePanning);
        if (healthApi) healthApi.trackRAF('recording', 'zoomPanLoop', animationFrameIdRef.current);
        return;
      }

      const scaleVal = zoomScaleRef.current;
      const limitX = W * (scaleVal - 1) / (2 * scaleVal);
      const limitY = H * (scaleVal - 1) / (2 * scaleVal);
      const currentTx = Math.max(-limitX, Math.min(limitX, zoomTxRef.current));
      const currentTy = Math.max(-limitY, Math.min(limitY, zoomTyRef.current));
      zoomTxRef.current = currentTx;
      zoomTyRef.current = currentTy;

      let targetVx = 0;
      let targetVy = 0;
      let isPanningActive = false;

      const distLeft = mouse.x;
      const distRight = W - mouse.x;
      const distTop = mouse.y - y_offset;
      const distBottom = (H + y_offset) - mouse.y;

      if (distRight < edgeThreshold && mouse.x > W - edgeThreshold) {
        const intensity = (edgeThreshold - distRight) / edgeThreshold;
        targetVx = -maxSpeed * intensity;
        isPanningActive = true;
      } else if (distLeft < edgeThreshold && mouse.x < edgeThreshold) {
        const intensity = (edgeThreshold - distLeft) / edgeThreshold;
        targetVx = maxSpeed * intensity;
        isPanningActive = true;
      }

      if (distBottom < edgeThreshold && mouse.y > H + y_offset - edgeThreshold) {
        const intensity = (edgeThreshold - distBottom) / edgeThreshold;
        targetVy = -maxSpeed * intensity;
        isPanningActive = true;
      } else if (distTop < edgeThreshold && mouse.y < y_offset + edgeThreshold) {
        const intensity = (edgeThreshold - distTop) / edgeThreshold;
        targetVy = maxSpeed * intensity;
        isPanningActive = true;
      }

      if (isPanningActive) {
        if (panTimeRef.current === 0) {
          panTimeRef.current = Date.now();
        }
      } else {
        panTimeRef.current = 0;
      }

      let speedMultiplier = 1;
      if (panTimeRef.current > 0) {
        const elapsed = Date.now() - panTimeRef.current;
        if (elapsed > 2000) {
          const secondsPast2s = (elapsed - 2000) / 1000;
          speedMultiplier = 1 + Math.min(2.0, secondsPast2s * 1.5);
        }
      }

      targetVx *= speedMultiplier;
      targetVy *= speedMultiplier;

      let currentEasing = easing;
      const activeDistances: number[] = [];
      if (distRight < edgeThreshold && mouse.x > W - edgeThreshold) activeDistances.push(distRight);
      if (distLeft < edgeThreshold && mouse.x < edgeThreshold) activeDistances.push(distLeft);
      if (distBottom < edgeThreshold && mouse.y > H + y_offset - edgeThreshold) activeDistances.push(distBottom);
      if (distTop < edgeThreshold && mouse.y < y_offset + edgeThreshold) activeDistances.push(distTop);

      if (activeDistances.length > 0) {
        const minDistance = Math.min(...activeDistances);
        if (minDistance < 60) {
          const ratio = (60 - minDistance) / 60;
          currentEasing = easing + (0.18 - easing) * ratio;
        }
      }

      const currentV = panVelocityRef.current;
      const vx = currentV.vx + (targetVx - currentV.vx) * currentEasing;
      const vy = currentV.vy + (targetVy - currentV.vy) * currentEasing;

      const finalVx = Math.abs(vx) < 0.05 && targetVx === 0 ? 0 : vx;
      const finalVy = Math.abs(vy) < 0.05 && targetVy === 0 ? 0 : vy;
      panVelocityRef.current = { vx: finalVx, vy: finalVy };

      if (finalVx !== 0 || finalVy !== 0) {
        const nextTx = Math.max(-limitX, Math.min(limitX, currentTx + finalVx));
        const nextTy = Math.max(-limitY, Math.min(limitY, currentTy + finalVy));

        if (nextTx !== currentTx || nextTy !== currentTy) {
          zoomTxRef.current = nextTx;
          zoomTyRef.current = nextTy;
          setPanTrigger(prev => prev + 1);
        }
      }

      animationFrameIdRef.current = requestAnimationFrame(updatePanning);
      // Track RAF in health
      if (healthApi && animationFrameIdRef.current) {
        healthApi.trackRAF('recording', 'zoomPanLoop', animationFrameIdRef.current);
      }
    };

    animationFrameIdRef.current = requestAnimationFrame(updatePanning);
    if (healthApi && animationFrameIdRef.current) {
      healthApi.trackRAF('recording', 'zoomPanLoop', animationFrameIdRef.current);
    }

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        if (healthApi) healthApi.untrackRAF('zoomPanLoop');
        animationFrameIdRef.current = null;
      }
    };
  }, [isZoomed, zoomEnabled]);

  return {
    zoomEnabled,
    setZoomEnabled,
    isZoomed,
    setIsZoomed,
    zoomScale,
    setZoomScale,
    zoomTxRef,
    zoomTyRef,
    zoomTransitioning,
    panTrigger
  };
}
